import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ArrowLeft, Shield, Mail, Smartphone, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';

const Login = () => {
  const navigate = useNavigate();
  const { login, verify2fa } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // 2FA state
  const [show2fa, setShow2fa] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [twoFactorMethod, setTwoFactorMethod] = useState('');
  const [code, setCode] = useState('');
  const [resending, setResending] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else if (result.requires_2fa) {
      setShow2fa(true);
      setTwoFactorToken(result.two_factor_token);
      setTwoFactorMethod(result.two_factor_method);
      setCode('');
    } else {
      setError(result.error);
    }
  };

  const handleVerify2fa = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setError('');
    setLoading(true);
    const result = await verify2fa(twoFactorToken, code);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/2fa/resend-email-code`, {
        two_factor_token: twoFactorToken
      });
      toast.success('A new code has been sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  if (show2fa) {
    return (
      <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center p-4" data-testid="2fa-challenge-page">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-sm border border-[#E5E4E0] overflow-hidden">
            <div className="bg-[#4B6651] p-6 text-center">
              <Shield className="w-10 h-10 text-white mx-auto mb-2" />
              <h1 className="text-xl font-bold text-white tracking-tight">Two-Factor Authentication</h1>
              <p className="text-white/70 text-sm mt-1">
                {twoFactorMethod === 'totp' ? 'Enter the code from your authenticator app' : 'Enter the code sent to your email'}
              </p>
            </div>
            <div className="p-6">
              <form onSubmit={handleVerify2fa} className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-[#F8F7F4] rounded-lg text-sm text-[#5C5F5C]">
                  {twoFactorMethod === 'totp' ? (
                    <><Smartphone className="w-4 h-4 flex-shrink-0" /> Open your authenticator app and enter the 6-digit code</>
                  ) : (
                    <><Mail className="w-4 h-4 flex-shrink-0" /> A verification code was sent to your email</>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-[#2A2C2A] text-sm font-medium">Verification Code</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    className="text-center text-2xl tracking-[0.3em] font-mono border-[#E5E4E0] bg-white h-14"
                    autoFocus
                    data-testid="2fa-code-input"
                  />
                </div>
                {error && <p className="text-sm text-[#D94F4F]" data-testid="2fa-error">{error}</p>}
                <Button
                  type="submit"
                  disabled={loading || code.length < 6}
                  className="w-full bg-[#4B6651] hover:bg-[#3E5543] text-white h-11"
                  data-testid="2fa-verify-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Verify
                </Button>
                <div className="flex items-center justify-between text-xs">
                  {twoFactorMethod === 'email' && (
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resending}
                      className="text-[#4B6651] hover:underline"
                      data-testid="resend-2fa-code-btn"
                    >
                      {resending ? 'Sending...' : 'Resend code'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { setShow2fa(false); setError(''); setCode(''); }}
                    className="text-[#5C5F5C] hover:underline flex items-center gap-1"
                    data-testid="back-to-login-btn"
                  >
                    <ArrowLeft className="w-3 h-3" /> Back to login
                  </button>
                </div>
                <p className="text-xs text-center text-[#5C5F5C]">
                  You can also use a backup code
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-sm border border-[#E5E4E0] overflow-hidden">
          <div className="bg-[#4B6651] p-6 text-center">
            <h1 className="text-2xl font-bold text-white tracking-tight">VetNeuroHelp</h1>
            <p className="text-white/70 text-sm mt-1">AI-Powered Neurological Diagnostics</p>
          </div>
          <div className="p-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#2A2C2A] text-sm font-medium">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border-[#E5E4E0] bg-white h-11"
                  data-testid="login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#2A2C2A] text-sm font-medium">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="border-[#E5E4E0] bg-white h-11 pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5F5C] hover:text-[#2A2C2A]"
                    data-testid="login-toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Link to="/forgot-password" className="text-xs text-[#4B6651] hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>

              {error && <p className="text-sm text-[#D94F4F]" data-testid="login-error">{error}</p>}

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#4B6651] hover:bg-[#3E5543] text-white h-11"
                data-testid="login-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Sign In
              </Button>

              <p className="text-center text-sm text-[#5C5F5C]">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#4B6651] font-medium hover:underline" data-testid="register-link">
                  Sign Up
                </Link>
              </p>
            </form>
          </div>
          <div className="border-t border-[#E5E4E0] p-4 text-center">
            <p className="text-xs text-[#5C5F5C]">
              By signing in, you agree to our{' '}
              <Link to="/terms" className="text-[#4B6651] hover:underline">Terms</Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-[#4B6651] hover:underline">Privacy Policy</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
